import React, { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Check, CreditCard, KeyRound, LogIn, LogOut, Mail, MoreVertical, PanelRight, Plus, Shield, Trash2, X } from "lucide-react";
import api from "@/api/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetDescription, SheetOverlay, SheetPortal, SheetTitle } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AppTile, BrandStyles, TINT_COLORS, VARIABILI_SEZIONE } from "../components/integrations/brand";
import { AloneHover, BORDO_ATTIVO, BORDO_SCELTO } from "../components/billing/SchedaPiano";
import { PER_CHI, SU_MISURA, aspettoPiano, euro, selfService } from "../components/billing/piani";
import {
  BOTTONE,
  GRIGIO,
  SCURO,
  IntestazioneCasella,
  Interruttore,
  Paginazione,
  Ricerca,
} from "../components/integrations/casella";
import { CAMPO_TESTO, Campo } from "../components/widget/campi";
import { iniziali } from "../components/contacts/recapiti";

const PER_PAGINA = 25;
// Quanto aspettare dopo l'ultima lettera prima di cercare.
const ATTESA_RICERCA = 250;
const SENZA_PIANO = "nessuno";
const COLONNE = 12;
// Un dato per colonna: quando non ci stanno tutte la tabella scorre di lato,
// e il cliente (a sinistra) e le azioni (a destra) restano fermi.
const FERMA_A_SINISTRA = "sm:sticky sm:left-0 sm:z-[1] sm:shadow-[1px_0_0_#F1F5F9]";
const FERMA_A_DESTRA = "sticky right-0 z-[1] shadow-[-1px_0_0_#F1F5F9]";
// Le celle ferme hanno un fondo loro: segue quello della riga.
const FONDO_RIGA = "bg-white group-hover:bg-[#F8FAFC] group-data-[state=selected]:bg-[#F8FAFC]";
const TUTTI = "tutti";
const GIORNO = 24 * 60 * 60 * 1000;

const errore = (e) => e?.response?.data?.message || e?.message || "Riprova fra poco.";
const nomeDi = (cliente) => cliente.name || cliente.email;
// Le iniziali dal nome o, se manca, dall'email (stessa regola dei contatti).
const inizialiDi = (cliente) => iniziali({ headline: nomeDi(cliente) });

/** La data di registrazione: "20 ott", con l'anno se non e' quest'anno. */
function dataBreve(valore) {
  const data = new Date(valore);
  const stessoAnno = data.getFullYear() === new Date().getFullYear();
  return data.toLocaleDateString("it-IT", { day: "numeric", month: "short", ...(stessoAnno ? {} : { year: "numeric" }) });
}

/** Rinnovi e scadenze: sempre con l'anno, "21 ott 2027". */
const dataConAnno = (valore) =>
  new Date(valore).toLocaleDateString("it-IT", { day: "numeric", month: "short", year: "numeric" });

const conta = (n, uno, tanti) => `${n} ${n === 1 ? uno : tanti}`;

/** Quanti giorni di calendario mancano a una data (negativi se e' passata). */
function giorniA(valore) {
  const inizio = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  return Math.round((inizio(new Date(valore)) - inizio(new Date())) / GIORNO);
}

/** "Scade oggi", "Scade domani", "Scade tra 12 giorni". */
function scadeTra(valore) {
  const giorni = giorniA(valore);
  if (giorni <= 0) return "Scade oggi";
  if (giorni === 1) return "Scade domani";
  return `Scade tra ${giorni} giorni`;
}

/** "scaduto oggi", "scaduto ieri", "scaduto da 3 giorni". */
function scadutoDa(valore) {
  const giorni = -giorniA(valore);
  if (giorni <= 0) return "scaduto oggi";
  if (giorni === 1) return "scaduto ieri";
  return `scaduto da ${giorni} giorni`;
}

// Come paga il cliente: con Stripe, venduto da PingUp, sul piano gratuito.
const CANALI = {
  online: { etichetta: "Online", spiegazione: "Online, con Stripe", tono: "blu" },
  offline: { etichetta: "Offline", spiegazione: "Offline, venduto da PingUp", tono: "viola" },
  gratuito: { etichetta: "Gratuito", spiegazione: "Piano gratuito", tono: "neutro" },
};

const RUOLI = { admin: "Amministratore", member: "Membro" };

// Accanto ai canali, i clienti per cosa c'e' da fare. Il pallino si
// colora solo quando ce n'e' almeno uno.
const VISTE = [
  { valore: "in_scadenza", etichetta: "In scadenza", numero: (s) => s.expiring_soon, tono: "attenzione" },
  { valore: "da_sistemare", etichetta: "Scaduti o non pagati", numero: (s) => s.needs_attention, tono: "errore" },
  { valore: "senza_scadenza", etichetta: "Senza scadenza", numero: (s) => s.no_end_date, tono: "attenzione" },
  { valore: "senza_piano", etichetta: "Senza piano", numero: (s) => s.no_plan, tono: "neutro" },
];

const PUNTI = {
  bene: "bg-[#19D163]",
  attenzione: "bg-[#F59E0B]",
  errore: "bg-[#EF4444]",
  neutro: "bg-[#94A3B8]",
  blu: "bg-[#3B82F6]",
  viola: "bg-[#A855F7]",
};

/**
 * I clienti di PingUp, solo per gli amministratori: una riga per cliente
 * (chi possiede almeno un'organizzazione, dal piu' recente), un dato per
 * colonna. Sopra, i filtri per canale e per cosa c'e' da fare (in scadenza,
 * scaduti o non pagati, senza piano). Organizzazioni, persone e consumi stanno nella
 * scheda che si apre a destra cliccando la riga.
 */
export default function AdminUsers() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { user: io, tenants } = useAuth();

  const [pagina, setPagina] = useState(1);
  const [cerca, setCerca] = useState("");
  const [q, setQ] = useState("");
  const [vista, setVista] = useState(TUTTI);
  const [canale, setCanale] = useState(TUTTI);
  const [scheda, setScheda] = useState(null);
  const [nuovaAperta, setNuovaAperta] = useState(false);
  const [conferma, setConferma] = useState(null);

  useEffect(() => {
    const attesa = setTimeout(() => {
      setQ(cerca.trim());
      setPagina(1);
    }, ATTESA_RICERCA);
    return () => clearTimeout(attesa);
  }, [cerca]);

  const elenco = useQuery({
    queryKey: ["admin-users", { pagina, q, vista, canale }],
    queryFn: async () => {
      const ricerca = new URLSearchParams({ page: String(pagina), limit: String(PER_PAGINA) });
      if (q) ricerca.set("search", q);
      if (vista !== TUTTI) ricerca.set("vista", vista);
      if (canale !== TUTTI) ricerca.set("canale", canale);
      return (await api.get(`/admin/users?${ricerca}`)).data;
    },
    placeholderData: (prev) => prev, // resta visibile la pagina precedente durante il caricamento (rq v5)
  });
  const clienti = elenco.data?.users || [];
  const stats = elenco.data?.stats;

  const { data: catalogo } = useQuery({
    queryKey: ["plans"],
    queryFn: async () => (await api.get("/billing/catalog")).data,
  });
  const piani = catalogo?.plans || [];

  // La scheda segue l'elenco: dopo un cambio di piano mostra i dati nuovi.
  const inScheda = scheda ? clienti.find((c) => String(c.id) === String(scheda.id)) || scheda : null;

  const aggiorna = () => queryClient.invalidateQueries({ queryKey: ["admin-users"] });

  const cambiaVista = (valore) => {
    setVista(valore);
    setPagina(1);
  };
  const cambiaCanale = (valore) => {
    setCanale(valore);
    setPagina(1);
  };

  // Entrare o uscire cambia le organizzazioni dell'amministratore: la pagina
  // si ricarica per aggiornarle ovunque (barra laterale, cambio organizzazione).
  const entra = useMutation({
    mutationFn: (tenantId) => api.post("/admin/join-tenant", { tenant_id: tenantId }),
    onSuccess: () => window.location.reload(),
    onError: (e) => toast({ title: "Non sei entrato nell'organizzazione", description: errore(e), variant: "destructive" }),
  });
  const esci = useMutation({
    mutationFn: (tenantId) => api.post("/admin/remove-member", { user_id: io?.id, tenant_id: tenantId }),
    onSuccess: () => window.location.reload(),
    onError: (e) => toast({ title: "Non sei uscito dall'organizzazione", description: errore(e), variant: "destructive" }),
  });
  const cancella = useMutation({
    mutationFn: (tenantId) => api.delete(`/admin/delete-organization/${tenantId}`),
    onSuccess: () => {
      const nome = conferma?.organizzazione?.name;
      // Era la sua unica organizzazione: il cliente non c'e' piu', la scheda si chiude.
      if (conferma?.cliente?.ownedTenants?.length === 1) setScheda(null);
      setConferma(null);
      aggiorna();
      toast({ title: "Organizzazione cancellata", description: nome ? `«${nome}» non c'è più.` : undefined });
    },
    onError: (e) => toast({ title: "Cancellazione non riuscita", description: errore(e), variant: "destructive" }),
  });
  const reimposta = useMutation({
    mutationFn: (userId) => api.post("/admin/reset-password", { user_id: userId }),
    onSuccess: (risposta) => {
      setConferma(null);
      toast({ title: "Password reimpostata", description: `Abbiamo mandato quella nuova a ${risposta.data?.user?.email}.` });
    },
    onError: (e) => toast({ title: "Password non reimpostata", description: errore(e), variant: "destructive" }),
  });

  const vaiA = (tenantId) => {
    localStorage.setItem("activeTenantId", String(tenantId));
    window.location.href = "/home";
  };

  const sonoDentro = (tenantId) =>
    (tenants || []).some((t) => String(t.tenant_id || t._id) === String(tenantId));

  // Le azioni di un cliente, uguali nel menu della riga e nella scheda.
  const azioni = (cliente) => ({
    sonoIo: String(cliente.id) === String(io?.id),
    sonoDentro,
    inCorso: entra.isPending || esci.isPending,
    onEntra: (org) => entra.mutate(org.id),
    onVai: (org) => vaiA(org.id),
    onEsci: (org) => esci.mutate(org.id),
    onCancella: (org) => setConferma({ tipo: "cancella", cliente, organizzazione: org }),
    onReimposta: () => setConferma({ tipo: "password", cliente }),
    onCambiaPiano: () => setConferma({ tipo: "piano", cliente }),
  });

  const voci = [
    { valore: TUTTI, etichetta: "Tutti" },
    { valore: "online", etichetta: "Online", numero: stats?.online ?? null },
    { valore: "offline", etichetta: "Offline", numero: stats?.offline ?? null },
    { valore: "gratuito", etichetta: "Gratuito", numero: stats?.free ?? null },
  ];

  return (
    <div className="relative isolate" style={VARIABILI_SEZIONE}>
      <BrandStyles />

      <IntestazioneCasella
        icona={Shield}
        titolo="Clienti"
        dettagli={stats?.clients ? <Conteggi stats={stats} /> : null}
        azioni={
          <button type="button" className={SCURO} onClick={() => setNuovaAperta(true)}>
            <Plus className="h-3.5 w-3.5" aria-hidden="true" />
            Nuova organizzazione
          </button>
        }
      />

      <div className="pu-fade-up mx-auto max-w-[1400px] space-y-4 pb-10">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex max-w-full flex-wrap items-center gap-2">
            <div className="max-w-full overflow-x-auto">
              <Interruttore etichetta="Come pagano" valore={canale} onCambia={cambiaCanale} voci={voci} />
            </div>
            <div className="max-w-full overflow-x-auto">
              <Situazioni stats={stats} vista={vista} onVista={cambiaVista} />
            </div>
          </div>
          <Ricerca valore={cerca} onCambia={setCerca} segnaposto="Cerca per nome o email" etichetta="Cerca tra i clienti" />
        </div>

        {elenco.isError ? (
          <div className="rounded-[14px] border border-[#FECACA] bg-[#FEF2F2] px-4 py-3 text-sm text-[#B91C1C]">
            <b className="block font-medium text-[#991B1B]">Elenco dei clienti non disponibile</b>
            {errore(elenco.error)}
          </div>
        ) : (
          <div className="overflow-hidden rounded-[14px] border border-[#E2E8F0] bg-white">
            <Table className="[&_td]:px-3 [&_th]:px-3">
              <TableHeader className="bg-[#F8FAFC]">
                <TableRow className="border-[#E2E8F0] hover:bg-transparent">
                  <Intestazione className={`bg-[#F8FAFC] ${FERMA_A_SINISTRA}`}>Cliente</Intestazione>
                  <Intestazione>Email</Intestazione>
                  <Intestazione>Origine</Intestazione>
                  <Intestazione>Piano</Intestazione>
                  <Intestazione>Come paga</Intestazione>
                  <Intestazione>Stato</Intestazione>
                  <Intestazione>Scadenza</Intestazione>
                  <Intestazione numero>Organizzazioni</Intestazione>
                  <Intestazione numero>Utenti</Intestazione>
                  <Intestazione numero>Inviti</Intestazione>
                  <Intestazione>Cliente dal</Intestazione>
                  <TableHead className={`h-10 w-[1%] bg-[#F8FAFC] ${FERMA_A_DESTRA}`}>
                    <span className="sr-only">Azioni</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {elenco.isLoading ? (
                  <RigheInCaricamento />
                ) : clienti.length ? (
                  clienti.map((cliente) => (
                    <RigaCliente
                      key={cliente.id}
                      cliente={cliente}
                      aperta={String(inScheda?.id) === String(cliente.id)}
                      onApri={() => setScheda(cliente)}
                      {...azioni(cliente)}
                    />
                  ))
                ) : (
                  <TableRow className="hover:bg-transparent">
                    <TableCell colSpan={COLONNE} className="py-12">
                      <ElencoVuoto
                        q={q}
                        vista={canale === TUTTI ? vista : TUTTI}
                        filtrato={vista !== TUTTI || canale !== TUTTI}
                        onAzzeraRicerca={() => setCerca("")}
                        onTutti={() => {
                          cambiaVista(TUTTI);
                          cambiaCanale(TUTTI);
                        }}
                        onNuova={() => setNuovaAperta(true)}
                      />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <Paginazione paginazione={elenco.data?.pagination} perPagina={PER_PAGINA} onPagina={setPagina} cosa="Clienti" />
          </div>
        )}
      </div>

      <SchedaCliente cliente={inScheda} onChiudi={() => setScheda(null)} {...(inScheda ? azioni(inScheda) : {})} />

      <NuovaOrganizzazione
        aperta={nuovaAperta}
        onChiudi={() => setNuovaAperta(false)}
        piani={piani}
        onCreata={(risposta) => {
          setNuovaAperta(false);
          // Il cliente nuovo e' il piu' recente: in cima alla prima pagina, senza ricerca ne' filtri.
          setCerca("");
          setVista(TUTTI);
          setCanale(TUTTI);
          setPagina(1);
          aggiorna();
          toast({ title: "Organizzazione creata", description: `Abbiamo mandato l'invito a ${risposta.user.email}.` });
        }}
      />

      <CambiaPiano
        cliente={conferma?.tipo === "piano" ? conferma.cliente : null}
        piani={piani}
        onChiudi={() => setConferma(null)}
        onSalvato={(aggiornato) => {
          setConferma(null);
          aggiorna();
          toast({
            title: "Piano aggiornato",
            description: `${nomeDi(aggiornato)} ora ha ${aggiornato.subscription?.plan?.name || "il piano scelto"}.`,
          });
        }}
      />

      <Dialog open={conferma?.tipo === "cancella"} onOpenChange={(open) => !open && setConferma(null)}>
        <DialogContent className="max-w-md rounded-[14px]">
          <DialogHeader>
            <DialogTitle>Cancellare «{conferma?.organizzazione?.name}»?</DialogTitle>
            <DialogDescription>
              Vengono rimossi per sempre gli assistenti con le loro fonti e conversazioni, i membri, le statistiche del
              widget, il conto di fatturazione e l'abbonamento.
              {conferma?.cliente?.ownedTenants?.length === 1
                ? ` Era l'unica organizzazione di ${nomeDi(conferma.cliente)}: non comparirà più tra i clienti.`
                : ""}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <button type="button" className={GRIGIO} onClick={() => setConferma(null)}>
              Annulla
            </button>
            <button
              type="button"
              className={`${BOTTONE} bg-[#DC2626] text-white hover:bg-[#B91C1C]`}
              onClick={() => cancella.mutate(conferma.organizzazione.id)}
              disabled={cancella.isPending}
            >
              {cancella.isPending ? "Cancellazione…" : "Cancella per sempre"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={conferma?.tipo === "password"} onOpenChange={(open) => !open && setConferma(null)}>
        <DialogContent className="max-w-md rounded-[14px]">
          <DialogHeader>
            <DialogTitle>Reimpostare la password di {conferma?.cliente ? nomeDi(conferma.cliente) : ""}?</DialogTitle>
            <DialogDescription>
              Ne generiamo una nuova e la mandiamo a {conferma?.cliente?.email}. Quella di adesso smette subito di
              funzionare.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <button type="button" className={GRIGIO} onClick={() => setConferma(null)}>
              Annulla
            </button>
            <button
              type="button"
              className={SCURO}
              onClick={() => reimposta.mutate(conferma.cliente.id)}
              disabled={reimposta.isPending}
            >
              {reimposta.isPending ? "Invio…" : "Reimposta e invia"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Accanto al titolo: quanti clienti e quanti nuovi questo mese. */
function Conteggi({ stats }) {
  return (
    <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-[#475569]">
      <span>
        <strong className="font-semibold tabular-nums text-[#020617]">{stats.clients}</strong>{" "}
        {stats.clients === 1 ? "cliente" : "clienti"}
      </span>
      {stats.this_month ? (
        <>
          <span className="text-[#CBD5E1]" aria-hidden="true">
            ·
          </span>
          <span>
            <strong className="font-semibold tabular-nums text-[#020617]">{stats.this_month}</strong>{" "}
            {stats.this_month === 1 ? "nuovo" : "nuovi"} questo mese
          </span>
        </>
      ) : null}
    </p>
  );
}

/**
 * In scadenza, Scaduti o non pagati, Senza piano: come i canali, ma se ne sceglie
 * al massimo uno e un altro clic lo toglie. Si somma al canale scelto.
 */
function Situazioni({ stats, vista, onVista }) {
  return (
    <div role="group" aria-label="Cosa c'è da fare" className="inline-flex self-start rounded-[12px] bg-[#F1F5F9] p-1">
      {VISTE.map((voce) => {
        const numero = stats ? voce.numero(stats) ?? 0 : null;
        const attiva = vista === voce.valore;
        return (
          <button
            key={voce.valore}
            type="button"
            aria-pressed={attiva}
            onClick={() => onVista(attiva ? TUTTI : voce.valore)}
            className={`inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-[9px] px-3 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
              attiva ? "bg-white text-[#020617] shadow-[0_1px_2px_rgba(15,23,42,0.08)]" : "text-[#64748B] hover:text-[#020617]"
            }`}
          >
            <span className={`h-1.5 w-1.5 rounded-full ${numero > 0 ? PUNTI[voce.tono] : "bg-[#CBD5E1]"}`} aria-hidden="true" />
            {voce.etichetta}
            {numero !== null ? (
              <span className={`text-xs tabular-nums ${attiva ? "text-[#64748B]" : "text-[#94A3B8]"}`}>{numero}</span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}

function Intestazione({ numero = false, className = "", children }) {
  return (
    <TableHead className={`h-10 whitespace-nowrap text-xs font-medium text-[#64748B] ${numero ? "text-right" : ""} ${className}`}>
      {children}
    </TableHead>
  );
}

/** Un segno piccolo a pillola: grigio di norma, verde se va bene, ambra o rosso per attirare l'occhio. */
// Piano, canale e stato usano la stessa pillola: cambia solo il colore.
function Segno({ children, tono = "neutro", punto = tono !== "neutro" && tono !== "piano", title }) {
  const colori = {
    neutro: "bg-[#F1F5F9] text-[#475569]",
    piano: "bg-[#F1F5F9] font-semibold text-[#020617]",
    bene: "bg-[#19D163]/10 text-[#0F7A3D]",
    attenzione: "bg-[#FFFBEB] text-[#92400E]",
    errore: "bg-[#FEF2F2] text-[#B91C1C]",
    blu: "bg-[#EFF6FF] text-[#1D4ED8]",
    viola: "bg-[rgba(173,70,255,0.12)] text-[#9333EA]",
  };
  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium leading-4 ${colori[tono]}`}
      title={title}
    >
      {punto ? <span className={`h-1.5 w-1.5 rounded-full ${PUNTI[tono]}`} aria-hidden="true" /> : null}
      {children}
    </span>
  );
}

// Una cella vuota: il dato non c'e' o non vale per questo cliente.
const Nessuno = () => <span className="text-[#CBD5E1]">—</span>;

/** Nella tabella, accanto al nome: lo scudo viola degli amministratori di PingUp. */
function SegnoAmministratore() {
  return (
    <span className="shrink-0 text-[#A855F7]" title="Amministratore di PingUp">
      <Shield className="h-3.5 w-3.5" aria-hidden="true" />
      <span className="sr-only">Amministratore</span>
    </span>
  );
}

/** Il nome, o "Senza nome" quando al posto del nome c'e' l'email (che sta sotto). */
function Nome({ nome, email, className = "" }) {
  return nome && nome !== email ? (
    <span className={`truncate text-[#020617] ${className}`}>{nome}</span>
  ) : (
    <span className={`truncate text-[#94A3B8] ${className}`}>Senza nome</span>
  );
}

function Iniziali({ testo, piccole = false }) {
  return (
    <span
      className={`flex shrink-0 items-center justify-center rounded-full bg-[#F1F5F9] font-semibold text-[#334155] ${
        piccole ? "h-7 w-7 text-[10px]" : "h-9 w-9 text-xs"
      }`}
      aria-hidden="true"
    >
      {testo}
    </span>
  );
}

/** Come e' arrivato il cliente: dal sito, creato da noi, dalla piattaforma PingUp. */
function origineDi(registrazione) {
  if (!registrazione) return null;
  if (registrazione.source === "admin") {
    const chi = registrazione.created_by?.name || registrazione.created_by?.email;
    return chi ? `Creato da ${chi}` : "Creato da PingUp";
  }
  if (registrazione.source === "sso") return "Piattaforma PingUp";
  return registrazione.sso_linked ? "Dal sito, poi SSO" : "Dal sito";
}

/** Lo stato del cliente, dal piu' urgente: uno solo. */
function statoDi(cliente) {
  const abbonamento = cliente.subscription || {};
  if (abbonamento.status === "incomplete") return { testo: "Pagamento in sospeso", tono: "errore" };
  if (abbonamento.status === "past_due") return { testo: "Rinnovo non pagato", tono: "errore" };
  if (abbonamento.expired) return { testo: "Scaduto", tono: "errore" };
  if (!abbonamento.plan) return { testo: "Senza piano", tono: "neutro" };
  if (abbonamento.status === "canceled") return { testo: "Chiuso", tono: "neutro" };
  if (abbonamento.cancel_at_period_end) return { testo: "Disdetto", tono: "attenzione" };
  if (abbonamento.expiring_soon) return { testo: scadeTra(abbonamento.current_period_end), tono: "attenzione" };
  if (cliente.registration?.invite_pending) return { testo: "Invito da accettare", tono: "attenzione" };
  return { testo: "Attivo", tono: "bene" };
}

function Stato({ cliente }) {
  const { testo, tono } = statoDi(cliente);
  return <Segno tono={tono}>{testo}</Segno>;
}

/** Il nome del piano; con un cambio programmato al rinnovo, "PLUS → LITE". */
function NomePiano({ abbonamento, vuoto = <span className="whitespace-nowrap text-[#94A3B8]">Nessun piano</span> }) {
  if (!abbonamento?.plan) return vuoto;
  const cambio = abbonamento.scheduled_change;
  return (
    <Segno tono="piano">
      {abbonamento.plan.name}
      {cambio ? (
        <span className="font-medium text-[#64748B]" title={`Dal ${dataConAnno(cambio.at)} passa a ${cambio.plan.name}`}>
          {" "}
          → {cambio.plan.name}
        </span>
      ) : null}
    </Segno>
  );
}

// Un piano a pagamento: c'e', non e' il gratuito (anche se assegnato da noi).
const aPagamento = (abbonamento) =>
  Boolean(abbonamento?.plan) && abbonamento.channel !== "gratuito" && abbonamento.plan.key !== "plan_basic";

/** Come paga, nella pillola: blu online, viola offline, grigio gratuito. */
function Canale({ abbonamento, esteso = false }) {
  if (!abbonamento?.plan) return null;
  const canale = CANALI[abbonamento.channel] || CANALI.gratuito;
  return (
    <Segno tono={canale.tono} punto>
      {esteso ? canale.spiegazione : canale.etichetta}
    </Segno>
  );
}

/** Sotto la data: fatturazione ricorrente, a scadenza, fine del contratto, quanto manca o da quanto e' scaduto. */
function notaScadenza(abbonamento) {
  if (abbonamento.channel === "online") return abbonamento.cancel_at_period_end ? "a scadenza" : "fatturazione ricorrente";
  if (abbonamento.expired) return scadutoDa(abbonamento.current_period_end);
  if (abbonamento.expiring_soon) return scadeTra(abbonamento.current_period_end).toLowerCase();
  return "fine del contratto";
}

function Scadenza({ abbonamento }) {
  if (!abbonamento?.plan) return <Nessuno />;
  // Un piano a pagamento senza data non scade mai: si vede, perche' manca la data.
  if (abbonamento.no_end_date) {
    return (
      <div className="whitespace-nowrap">
        <p className="text-[13px] text-[#B45309]">Senza scadenza</p>
        <p className="text-xs text-[#B45309]">manca la data di fine</p>
      </div>
    );
  }
  if (!abbonamento.current_period_end) {
    // Il gratuito non scade: niente da dire.
    return abbonamento.channel === "offline" && aPagamento(abbonamento) ? (
      <span className="whitespace-nowrap text-[13px] text-[#94A3B8]">Senza scadenza</span>
    ) : (
      <Nessuno />
    );
  }
  const colore = abbonamento.expired ? "text-[#B91C1C]" : abbonamento.expiring_soon ? "text-[#B45309]" : "text-[#64748B]";
  return (
    <div className="whitespace-nowrap">
      <p className="text-[13px] tabular-nums text-[#334155]">{dataConAnno(abbonamento.current_period_end)}</p>
      <p className={`text-xs ${colore}`}>{notaScadenza(abbonamento)}</p>
    </div>
  );
}

/**
 * L'azione che serve adesso, accanto al menu: rinnovare un contratto
 * offline scaduto o in scadenza, dare un piano a chi non ce l'ha, mettere
 * la data di fine a un piano a pagamento che non ce l'ha.
 */
function azioneDi(cliente) {
  const abbonamento = cliente.subscription || {};
  if (!cliente.ownedTenants?.length) return null;
  if (!abbonamento.plan) return "Assegna piano";
  if (abbonamento.channel !== "online" && (abbonamento.expired || abbonamento.expiring_soon)) return "Rinnova";
  if (abbonamento.no_end_date) return "Metti la scadenza";
  return null;
}

// Un clic sulla riga apre la scheda, ma non quando si usa un pulsante, un link o il menu.
const DENTRO_UN_COMANDO = "button, a, input, [role='menu'], [role='menuitem'], [role='dialog']";

/** "2 di 5": quante organizzazioni ha e quante gliene da' il piano (0 = illimitate). */
function Organizzazioni({ organizzazioni }) {
  const { count = 0, max = null } = organizzazioni || {};
  const oltre = max > 0 && count > max;
  return (
    <span
      className={`whitespace-nowrap tabular-nums ${oltre ? "font-medium text-[#B45309]" : "text-[#020617]"}`}
      title={oltre ? "Oltre il piano" : undefined}
    >
      {count}
      {max === null ? null : (
        <span className="text-[#94A3B8]">
          {" "}
          di {max === 0 ? <span title="Illimitate">∞</span> : max}
        </span>
      )}
    </span>
  );
}

const Numero = ({ valore }) => (valore ? <span className="tabular-nums text-[#020617]">{valore}</span> : <Nessuno />);

function RigaCliente({ cliente, aperta, onApri, ...azioni }) {
  const azione = azioneDi(cliente);
  const abbonamento = cliente.subscription;
  return (
    <TableRow
      data-state={aperta ? "selected" : undefined}
      className="group cursor-pointer border-[#F1F5F9] hover:bg-[#F8FAFC] data-[state=selected]:bg-[#F8FAFC]"
      onClick={(e) => !e.target.closest?.(DENTRO_UN_COMANDO) && onApri()}
    >
      <TableCell className={`py-2.5 ${FERMA_A_SINISTRA} ${FONDO_RIGA}`}>
        <div className="flex min-w-0 max-w-[220px] items-center gap-3">
          <Iniziali testo={inizialiDi(cliente)} />
          <button
            type="button"
            onClick={onApri}
            aria-label={`Apri la scheda di ${nomeDi(cliente)}`}
            className="min-w-0 truncate rounded-sm text-left text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Nome nome={cliente.name} email={cliente.email} />
          </button>
          {cliente.type === "admin" ? <SegnoAmministratore /> : null}
        </div>
      </TableCell>
      <TableCell className="max-w-[180px] truncate py-2.5 text-[13px] text-[#475569]" title={cliente.email}>
        {cliente.email}
      </TableCell>
      <TableCell className="whitespace-nowrap py-2.5 text-[13px] text-[#334155]">
        {origineDi(cliente.registration) || <Nessuno />}
      </TableCell>
      <TableCell className="py-2.5 text-sm">
        {/* Senza piano lo dice gia' lo stato. */}
        <NomePiano abbonamento={abbonamento} vuoto={<Nessuno />} />
      </TableCell>
      {/* Solo per i piani a pagamento: che sia gratuito lo dice gia' la colonna Piano. */}
      <TableCell className="py-2.5">{aPagamento(abbonamento) ? <Canale abbonamento={abbonamento} /> : <Nessuno />}</TableCell>
      <TableCell className="py-2.5">
        <Stato cliente={cliente} />
      </TableCell>
      <TableCell className="py-2.5">
        <Scadenza abbonamento={abbonamento} />
      </TableCell>
      <TableCell className="py-2.5 text-right text-sm">
        <Organizzazioni organizzazioni={cliente.organizations} />
      </TableCell>
      <TableCell className="py-2.5 text-right text-sm">
        <Numero valore={cliente.users?.count ?? 1} />
      </TableCell>
      <TableCell className="py-2.5 text-right text-sm">
        <Numero valore={cliente.users?.pending_invites} />
      </TableCell>
      <TableCell className="whitespace-nowrap py-2.5 text-[13px] tabular-nums text-[#64748B]">
        {dataBreve(cliente.createdAt)}
      </TableCell>
      <TableCell className={`py-2.5 ${FERMA_A_DESTRA} ${FONDO_RIGA}`}>
        <div className="flex items-center justify-end gap-1">
          {azione ? (
            <button type="button" className={`${GRIGIO} hidden h-8 whitespace-nowrap sm:inline-flex`} onClick={azioni.onCambiaPiano}>
              {azione}
            </button>
          ) : null}
          <AzioniCliente cliente={cliente} organizzazioni={cliente.ownedTenants || []} onApri={onApri} {...azioni} />
        </div>
      </TableCell>
    </TableRow>
  );
}

/**
 * Il menu a fine riga. Con piu' organizzazioni le azioni di ciascuna stanno
 * sotto il suo nome; cancellare resta in fondo, separato e in rosso.
 */
function AzioniCliente({ cliente, organizzazioni, onApri, sonoIo, sonoDentro, inCorso, onEntra, onVai, onEsci, onCancella, onReimposta, onCambiaPiano }) {
  const piu = organizzazioni.length > 1;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`Azioni per ${nomeDi(cliente)}`}
          className="flex h-8 w-8 items-center justify-center rounded-[8px] text-[#94A3B8] transition-colors hover:bg-[#F1F5F9] hover:text-[#334155] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <MoreVertical className="h-4 w-4" aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60 rounded-xl">
        <DropdownMenuItem onClick={onApri}>
          <PanelRight className="mr-2 h-4 w-4" aria-hidden="true" />
          Apri la scheda
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {organizzazioni.map((org) => {
          const dentro = sonoDentro(org.id);
          return (
            <React.Fragment key={org.id}>
              {piu ? (
                <DropdownMenuLabel className="truncate text-xs font-medium text-[#94A3B8]">{org.name}</DropdownMenuLabel>
              ) : null}
              {dentro ? (
                <DropdownMenuItem onClick={() => onVai(org)}>
                  <LogIn className="mr-2 h-4 w-4" aria-hidden="true" />
                  Vai all'organizzazione
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem onClick={() => onEntra(org)} disabled={inCorso}>
                  <LogIn className="mr-2 h-4 w-4" aria-hidden="true" />
                  Entra come amministratore
                </DropdownMenuItem>
              )}
              {dentro && !sonoIo ? (
                <DropdownMenuItem onClick={() => onEsci(org)} disabled={inCorso}>
                  <LogOut className="mr-2 h-4 w-4" aria-hidden="true" />
                  Esci dall'organizzazione
                </DropdownMenuItem>
              ) : null}
            </React.Fragment>
          );
        })}
        {organizzazioni.length ? <DropdownMenuSeparator /> : null}
        {organizzazioni.length ? (
          <DropdownMenuItem onClick={onCambiaPiano}>
            <CreditCard className="mr-2 h-4 w-4" aria-hidden="true" />
            Cambia piano
          </DropdownMenuItem>
        ) : null}
        <DropdownMenuItem asChild>
          <a href={`mailto:${cliente.email}`}>
            <Mail className="mr-2 h-4 w-4" aria-hidden="true" />
            Scrivi un'email
          </a>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onReimposta}>
          <KeyRound className="mr-2 h-4 w-4" aria-hidden="true" />
          Reimposta la password
        </DropdownMenuItem>
        {organizzazioni.length ? <DropdownMenuSeparator /> : null}
        {organizzazioni.map((org) => (
          <DropdownMenuItem key={org.id} className="text-red-600 focus:text-red-600" onClick={() => onCancella(org)}>
            <Trash2 className="mr-2 h-4 w-4" aria-hidden="true" />
            <span className="truncate">{piu ? `Cancella «${org.name}»` : "Cancella l'organizzazione"}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ---------------------------------------------------------------------------
// Scheda del cliente
// ---------------------------------------------------------------------------

// I limiti su misura in breve: "Assistenti 3 · Fonti illimitate".
function limitiInBreve(limiti) {
  return LIMITI.filter(({ campo }) => limiti?.[campo] != null)
    .map(({ campo, etichetta }) => `${etichetta} ${limiti[campo] === 0 ? "illimitati" : new Intl.NumberFormat("it-IT").format(limiti[campo])}`)
    .join(" · ");
}

/** Una riga della scheda: etichetta a sinistra, valore a destra. */
function Voce({ etichetta, children }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2">
      <dt className="shrink-0 text-[13px] text-[#64748B]">{etichetta}</dt>
      <dd className="min-w-0 text-right text-[13px] text-[#020617]">{children}</dd>
    </div>
  );
}

function TitoloParte({ children }) {
  return <h3 className="text-xs font-semibold uppercase tracking-[0.06em] text-[#94A3B8]">{children}</h3>;
}

/**
 * La scheda a destra: abbonamento, chi e' il cliente, e le sue
 * organizzazioni con consumi, persone e azioni.
 */
function SchedaCliente({ cliente, onChiudi, sonoIo, sonoDentro, inCorso, onEntra, onVai, onEsci, onCancella, onReimposta, onCambiaPiano }) {
  const contenuto = useRef(null);
  const abbonamento = cliente?.subscription || {};
  const organizzazioni = cliente?.ownedTenants || [];
  const azione = cliente ? azioneDi(cliente) : null;
  const { count = 0, max = null } = cliente?.organizations || {};
  const oltre = max > 0 && count > max;
  return (
    <Sheet open={Boolean(cliente)} onOpenChange={(open) => !open && onChiudi()}>
      <SheetPortal>
        <SheetOverlay className="bg-[#0F172A]/20" />
        <DialogPrimitive.Content
          ref={contenuto}
          tabIndex={-1}
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            contenuto.current?.focus();
          }}
          className="fixed inset-y-0 outline-none right-0 z-50 flex h-full w-full flex-col border-l border-[#E2E8F0] bg-white shadow-[0_0_40px_rgba(15,23,42,0.12)] transition ease-in-out data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right data-[state=closed]:duration-200 data-[state=open]:duration-300 sm:max-w-[460px]"
          aria-describedby={cliente ? "scheda-cliente-email" : undefined}
        >
          {cliente ? (
            <>
              <header className="flex items-start gap-3 border-b border-[#F1F5F9] px-5 py-4">
                <Iniziali testo={inizialiDi(cliente)} />
                <div className="min-w-0 flex-1">
                  <SheetTitle className="flex min-w-0 items-center gap-2 text-base font-semibold text-[#020617]">
                    <Nome nome={cliente.name} email={cliente.email} />
                    {cliente.type === "admin" ? <Segno>Amministratore</Segno> : null}
                  </SheetTitle>
                  <SheetDescription id="scheda-cliente-email" className="truncate text-[13px] text-[#64748B]">
                    {cliente.email}
                  </SheetDescription>
                </div>
                <DialogPrimitive.Close
                  aria-label="Chiudi la scheda"
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] text-[#94A3B8] transition-colors hover:bg-[#F1F5F9] hover:text-[#334155] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <X className="h-4 w-4" aria-hidden="true" />
                </DialogPrimitive.Close>
              </header>

              <div className="flex-1 space-y-6 overflow-y-auto px-5 py-5">
                <div className="flex flex-wrap gap-2">
                  {organizzazioni.length ? (
                    <button type="button" className={SCURO} onClick={onCambiaPiano}>
                      <CreditCard className="h-3.5 w-3.5" aria-hidden="true" />
                      {azione || "Cambia piano"}
                    </button>
                  ) : null}
                  <a href={`mailto:${cliente.email}`} className={GRIGIO}>
                    <Mail className="h-3.5 w-3.5" aria-hidden="true" />
                    Scrivi un'email
                  </a>
                </div>

                <section className="space-y-1" aria-labelledby="scheda-abbonamento">
                  <TitoloParte>
                    <span id="scheda-abbonamento">Abbonamento</span>
                  </TitoloParte>
                  <dl className="divide-y divide-[#F1F5F9]">
                    <Voce etichetta="Piano">
                      <NomePiano abbonamento={abbonamento} />
                      {abbonamento.scheduled_change ? (
                        <span className="block text-xs text-[#64748B]">
                          dal {dataConAnno(abbonamento.scheduled_change.at)} passa a {abbonamento.scheduled_change.plan.name}
                        </span>
                      ) : null}
                    </Voce>
                    {abbonamento.plan ? (
                      <Voce etichetta="Come paga">
                        <Canale abbonamento={abbonamento} esteso />
                      </Voce>
                    ) : null}
                    <Voce etichetta="Stato">
                      <Stato cliente={cliente} />
                    </Voce>
                    {abbonamento.plan && (abbonamento.current_period_end || abbonamento.channel === "offline") ? (
                      <Voce etichetta={abbonamento.channel === "online" && !abbonamento.cancel_at_period_end ? "Rinnovo" : "Scadenza"}>
                        <Scadenza abbonamento={abbonamento} />
                      </Voce>
                    ) : null}
                    {abbonamento.credit_eur > 0 ? (
                      <Voce etichetta="Credito Stripe">
                        {new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(abbonamento.credit_eur)}
                      </Voce>
                    ) : null}
                    {abbonamento.custom_limits ? (
                      <Voce etichetta="Limiti su misura">{limitiInBreve(abbonamento.custom_limits)}</Voce>
                    ) : null}
                  </dl>
                </section>

                <section className="space-y-1" aria-labelledby="scheda-cliente">
                  <TitoloParte>
                    <span id="scheda-cliente">Cliente</span>
                  </TitoloParte>
                  <dl className="divide-y divide-[#F1F5F9]">
                    <Voce etichetta="Cliente dal">
                      <span className="tabular-nums">{dataBreve(cliente.createdAt)}</span>
                    </Voce>
                    <Voce etichetta="Come è arrivato">{origineDi(cliente.registration) || <Nessuno />}</Voce>
                    <Voce etichetta="Organizzazioni">
                      <span className={`tabular-nums ${oltre ? "font-medium text-[#B45309]" : ""}`}>
                        {count}
                        {max === null ? null : <span className="text-[#94A3B8]"> di {max === 0 ? "illimitate" : max}</span>}
                      </span>
                      {oltre ? <span className="block text-xs text-[#B45309]">oltre il piano</span> : null}
                    </Voce>
                    <Voce etichetta="Persone">
                      <span className="tabular-nums">{cliente.users?.count ?? 1}</span>
                      {cliente.users?.pending_invites ? (
                        <span className="text-[#64748B]"> · {conta(cliente.users.pending_invites, "invito", "inviti")}</span>
                      ) : null}
                    </Voce>
                  </dl>
                </section>

                <section className="space-y-3" aria-labelledby="scheda-organizzazioni">
                  <TitoloParte>
                    <span id="scheda-organizzazioni">{organizzazioni.length === 1 ? "Organizzazione" : "Organizzazioni"}</span>
                  </TitoloParte>
                  {organizzazioni.map((org) => (
                    <SchedaOrganizzazione
                      key={org.id}
                      org={org}
                      conPrincipale={organizzazioni.length > 1}
                      dentro={sonoDentro(org.id)}
                      sonoIo={sonoIo}
                      inCorso={inCorso}
                      onEntra={() => onEntra(org)}
                      onVai={() => onVai(org)}
                      onEsci={() => onEsci(org)}
                      onCancella={() => onCancella(org)}
                    />
                  ))}
                </section>
              </div>

              <footer className="border-t border-[#F1F5F9] px-5 py-3">
                <button type="button" className={GRIGIO} onClick={onReimposta}>
                  <KeyRound className="h-3.5 w-3.5" aria-hidden="true" />
                  Reimposta la password
                </button>
              </footer>
            </>
          ) : null}
        </DialogPrimitive.Content>
      </SheetPortal>
    </Sheet>
  );
}

const CONSUMI = [
  { campo: "bots", etichetta: "Assistenti" },
  { campo: "sources", etichetta: "Fonti" },
  { campo: "messages_this_month", etichetta: "Risposte", nota: "nel mese" },
];

/** Un'organizzazione nella scheda: consumi, persone e cosa ci si puo' fare. */
function SchedaOrganizzazione({ org, conPrincipale, dentro, sonoIo, inCorso, onEntra, onVai, onEsci, onCancella }) {
  const collaboratori = org.collaboratori || [];
  return (
    <article className="rounded-[12px] border border-[#E2E8F0]" aria-label={org.name}>
      <div className="flex items-center gap-2 px-4 pt-3">
        <h4 className="min-w-0 truncate text-sm font-semibold text-[#020617]" title={org.name}>
          {org.name}
        </h4>
        {conPrincipale && org.is_master ? <Segno>principale</Segno> : null}
      </div>
      <dl className="grid grid-cols-3 gap-2 px-4 py-3">
        {CONSUMI.map(({ campo, etichetta, nota }) => (
          <div key={campo} className="min-w-0 rounded-[10px] bg-[#F8FAFC] px-3 py-2">
            <dt className="truncate text-[11px] text-[#64748B]">{etichetta}</dt>
            <dd className="truncate text-sm font-semibold tabular-nums text-[#020617]">
              {new Intl.NumberFormat("it-IT").format(org.usage?.[campo] || 0)}
              {nota ? <span className="text-[11px] font-normal text-[#94A3B8]"> {nota}</span> : null}
            </dd>
          </div>
        ))}
      </dl>
      <div className="border-t border-[#F1F5F9] px-4 py-2">
        {collaboratori.length ? (
          <ul aria-label={`Persone di ${org.name}`}>
            {collaboratori.map((persona) => (
              <li key={`${persona.email}-${persona.status}`} className="flex items-center gap-2 py-1.5">
                <Iniziali testo={iniziali({ headline: persona.name || persona.email })} piccole />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[13px]">
                    <Nome nome={persona.name} email={persona.email} />
                  </p>
                  <p className="truncate text-xs text-[#64748B]">{persona.email}</p>
                </div>
                {persona.status === "pending" ? (
                  <Segno tono="attenzione">Invito in attesa</Segno>
                ) : (
                  <Segno>{RUOLI[persona.role] || persona.role}</Segno>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="py-1.5 text-xs text-[#94A3B8]">Solo il titolare, nessun collaboratore.</p>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2 border-t border-[#F1F5F9] px-4 py-3">
        {dentro ? (
          <button type="button" className={GRIGIO} onClick={onVai}>
            <LogIn className="h-3.5 w-3.5" aria-hidden="true" />
            Vai all'organizzazione
          </button>
        ) : (
          <button type="button" className={GRIGIO} onClick={onEntra} disabled={inCorso}>
            <LogIn className="h-3.5 w-3.5" aria-hidden="true" />
            Entra come amministratore
          </button>
        )}
        {dentro && !sonoIo ? (
          <button type="button" className={GRIGIO} onClick={onEsci} disabled={inCorso}>
            <LogOut className="h-3.5 w-3.5" aria-hidden="true" />
            Esci
          </button>
        ) : null}
        <button
          type="button"
          className={`${BOTTONE} ml-auto text-[#DC2626] hover:bg-[#FEF2F2]`}
          onClick={onCancella}
          aria-label={`Cancella «${org.name}»`}
        >
          <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
          Cancella
        </button>
      </div>
    </article>
  );
}

/** Sagome grigie della stessa forma delle righe, mentre arriva l'elenco. */
function RigheInCaricamento() {
  const cella = (larghezza, chiave, numero = false) => (
    <TableCell key={chiave} className="py-3">
      <Skeleton className={`h-3 ${larghezza} ${numero ? "ml-auto" : ""}`} />
    </TableCell>
  );
  return Array.from({ length: 6 }).map((_, i) => (
    <TableRow key={i} className="border-[#F1F5F9] hover:bg-transparent">
      <TableCell className="py-3">
        <div className="flex items-center gap-3">
          <Skeleton className="h-9 w-9 rounded-full" />
          <Skeleton className="h-3.5 w-28" />
        </div>
      </TableCell>
      {cella("w-36", "email")}
      {cella("w-20", "origine")}
      {cella("w-14", "piano")}
      {cella("w-14", "canale")}
      <TableCell className="py-3">
        <Skeleton className="h-5 w-16 rounded-full" />
      </TableCell>
      {cella("w-20", "scadenza")}
      {["organizzazioni", "utenti", "inviti"].map((chiave) => cella("w-8", chiave, true))}
      {cella("w-14", "dal")}
      <TableCell className="py-3" />
    </TableRow>
  ));
}

// Il filtro scelto e' vuoto: una buona notizia, detta in chiaro.
const VISTE_VUOTE = {
  in_scadenza: "Nessun contratto scade nei prossimi 30 giorni.",
  da_sistemare: "Nessun cliente scaduto o non pagato.",
  senza_scadenza: "Tutti i piani a pagamento hanno una data di fine.",
  senza_piano: "Tutti i clienti hanno un piano.",
};

function ElencoVuoto({ q, vista, filtrato, onAzzeraRicerca, onTutti, onNuova }) {
  let testo = "Ancora nessun cliente. Quando qualcuno si registra, o crei tu un'organizzazione per un cliente, lo trovi qui.";
  if (q) testo = `Nessun cliente per «${q}».`;
  else if (VISTE_VUOTE[vista]) testo = VISTE_VUOTE[vista];
  else if (filtrato) testo = "Nessun cliente in questo elenco.";
  return (
    <div className="flex flex-col items-center gap-3 text-center">
      <p className="max-w-sm text-sm text-[#64748B]">{testo}</p>
      {q ? (
        <button type="button" className={GRIGIO} onClick={onAzzeraRicerca}>
          Cancella la ricerca
        </button>
      ) : filtrato ? (
        <button type="button" className={GRIGIO} onClick={onTutti}>
          Mostra tutti i clienti
        </button>
      ) : (
        <button type="button" className={SCURO} onClick={onNuova}>
          <Plus className="h-3.5 w-3.5" aria-hidden="true" />
          Nuova organizzazione
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cambia piano
// ---------------------------------------------------------------------------

// I limiti su misura dei piani Custom, nell'ordine in cui si leggono.
const LIMITI = [
  { campo: "widgets_max", etichetta: "Assistenti" },
  { campo: "sources_max", etichetta: "Fonti" },
  { campo: "messages_per_month", etichetta: "Risposte al mese" },
  { campo: "domains_max", etichetta: "Domini" },
  { campo: "max_organizations", etichetta: "Organizzazioni" },
];
const suMisura = (chiave) => String(chiave || "").startsWith("plan_custom");

/** Una data per <input type="date">, nel fuso di chi guarda: "2027-10-21". */
function perCampoData(valore) {
  if (!valore) return "";
  const d = new Date(valore);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// Il limite del piano come suggerimento nel campo vuoto.
const limiteDelPiano = (n) => (n === 0 ? "illimitati" : n == null ? "" : new Intl.NumberFormat("it-IT").format(n));

/** Il prezzo di un piano, in breve: "Gratis", "84 €/anno + IVA", "Prezzo su misura". */
function prezzoBreve(piano) {
  if (piano.price_yearly) return `${euro(piano.price_yearly)} €/anno + IVA`;
  return suMisura(piano.key) ? "Prezzo su misura" : "Gratis";
}

/** Cosa comprende un piano, in una riga; un su misura senza limiti fissi dice per chi e'. */
function inUnaRiga(piano) {
  const l = piano.limits || {};
  if (suMisura(piano.key) && !l.messages_per_month && !l.sources_max && !l.widgets_max) return PER_CHI[SU_MISURA];
  const quanti = (n, uno, tanti) => (n ? `${euro(n)} ${n === 1 ? uno : tanti}` : `${tanti} illimitati`);
  return [
    quanti(l.messages_per_month, "risposta al mese", "risposte al mese"),
    quanti(l.sources_max, "fonte", "fonti"),
    quanti(l.widgets_max, "sito o assistente", "siti o assistenti"),
  ].join(" · ");
}

/**
 * Un piano da scegliere, in piccolo: tessera, nome, prezzo e cosa comprende,
 * con i colori e i bordi delle schede della pagina Abbonamento. E' un radio:
 * le frecce passano da un piano all'altro.
 */
function PianoDaScegliere({ piano, attuale, scelto, onScegli }) {
  const aspetto = aspettoPiano(piano.key);
  const idDettagli = `piano-${piano.key}-dettagli`;
  return (
    <label className="relative block cursor-pointer">
      <input
        type="radio"
        name="piano"
        value={piano.key}
        checked={scelto}
        onChange={() => onScegli(piano.key)}
        aria-label={piano.name}
        aria-describedby={idDettagli}
        className="peer sr-only"
      />
      <div
        className={`group relative isolate flex h-full flex-col gap-2 rounded-[14px] border border-[#E2E8F0] bg-white p-4 transition-colors hover:border-[#CBD5E1] peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2 ${
          scelto ? BORDO_SCELTO : attuale ? BORDO_ATTIVO : ""
        }`}
      >
        <AloneHover colore={TINT_COLORS[aspetto.tint]} />
        <div className="flex items-center gap-3">
          <AppTile app={aspetto} size="sm" />
          <div className="min-w-0 flex-1">
            <p className="flex flex-wrap items-center gap-x-2 text-sm font-semibold text-[#020617]">
              {piano.name}
              {attuale ? <span className="text-xs font-medium text-[#0F7A3D]">attuale</span> : null}
            </p>
            <p className="text-xs text-[#64748B]">{prezzoBreve(piano)}</p>
          </div>
          <span
            aria-hidden="true"
            className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${
              scelto ? "border-[#18181B] bg-[#18181B] text-white" : "border-[#CBD5E1] bg-white"
            }`}
          >
            {scelto ? <Check className="h-3 w-3" strokeWidth={3} /> : null}
          </span>
        </div>
        <p id={idDettagli} className="text-xs leading-5 text-[#475569]">
          {inUnaRiga(piano)}
        </p>
      </div>
    </label>
  );
}

/**
 * Il piano di un cliente venduto da PingUp: si sceglie il piano (in piccolo,
 * con i colori della pagina Abbonamento), poi la fine del contratto e, per i
 * Custom, i limiti su misura. Vale per tutto l'account del cliente.
 * Chi paga con Stripe il piano lo cambia da solo: la finestra lo dice e basta.
 */
function CambiaPiano({ cliente, piani, onChiudi, onSalvato }) {
  const { toast } = useToast();
  const online = cliente?.subscription?.channel === "online";
  const attuale = cliente?.subscription?.plan?.key || "";
  const [dati, setDati] = useState({ piano: "", fine: "", limiti: {} });

  useEffect(() => {
    if (!cliente) return;
    const abbonamento = cliente.subscription || {};
    setDati({
      piano: abbonamento.plan?.key || "",
      fine: perCampoData(abbonamento.current_period_end),
      limiti: Object.fromEntries(LIMITI.map(({ campo }) => [campo, abbonamento.custom_limits?.[campo] ?? ""])),
    });
  }, [cliente]);

  const salva = useMutation({
    mutationFn: async (corpo) => (await api.put(`/admin/users/${cliente.id}/subscription`, corpo)).data,
    onSuccess: (risposta) => onSalvato(risposta.cliente),
    onError: (e) => toast({ title: "Piano non aggiornato", description: errore(e), variant: "destructive" }),
  });

  // Prima quelli che il cliente puo' scegliere da solo, poi i su misura.
  const inOrdine = [...piani.filter((piano) => selfService(piano.key)), ...piani.filter((piano) => !selfService(piano.key))];
  const scelto = piani.find((piano) => piano.key === dati.piano);
  const scegli = (chiave) => setDati((prima) => ({ ...prima, piano: chiave }));
  // Un piano a pagamento senza data di fine: si salva, ma non scade mai.
  const senzaData = Boolean(dati.piano) && dati.piano !== "plan_basic" && !dati.fine;
  const cambiaLimite = (campo, valore) => setDati((prima) => ({ ...prima, limiti: { ...prima.limiti, [campo]: valore } }));

  const invia = (e) => {
    e.preventDefault();
    if (!dati.piano) return;
    salva.mutate({
      plan_key: dati.piano,
      valid_until: dati.fine || null,
      // Vuoto = come il piano.
      limits: suMisura(dati.piano)
        ? Object.fromEntries(LIMITI.map(({ campo }) => [campo, dati.limiti[campo] === "" ? null : Number(dati.limiti[campo])]))
        : {},
    });
  };

  return (
    <Dialog open={Boolean(cliente)} onOpenChange={(open) => !open && onChiudi()}>
      <DialogContent
        className={`flex max-h-[90vh] w-[calc(100vw-2rem)] flex-col gap-0 overflow-hidden rounded-[14px] p-0 ${online ? "max-w-lg" : "max-w-2xl"}`}
      >
        <DialogHeader className="border-b border-[#F1F5F9] px-6 py-5 pr-12">
          {/* Senza nome ne' email (account vecchi) il titolo non resta a meta'. */}
          <DialogTitle>{cliente && nomeDi(cliente) ? `Piano di ${nomeDi(cliente)}` : "Piano del cliente"}</DialogTitle>
          <DialogDescription>
            {online
              ? "Paga online con Stripe."
              : "Vale per tutte le sue organizzazioni. Lo applichi tu, senza pagamento online."}
          </DialogDescription>
        </DialogHeader>
        {online ? (
          <>
            <p className="mx-6 my-5 rounded-[12px] bg-[#F8FAFC] px-4 py-3 text-sm leading-6 text-[#334155]">
              Ha {cliente?.subscription?.plan?.name || "un piano"} e lo gestisce lui dalla pagina Abbonamento: cambi,
              disdetta e pagamenti passano da Stripe. Per passarlo a un piano offline va prima disdetto l'abbonamento
              online.
            </p>
            <DialogFooter className="border-t border-[#F1F5F9] px-6 py-4">
              <button type="button" className={GRIGIO} onClick={onChiudi}>
                Chiudi
              </button>
            </DialogFooter>
          </>
        ) : (
          <form onSubmit={invia} noValidate className="flex min-h-0 flex-1 flex-col">
            <div className="min-h-0 flex-1 space-y-6 overflow-y-auto px-6 py-5">
              <fieldset>
                <legend className="mb-3 text-xs font-medium text-[#334155]">Piano</legend>
                <div className="grid gap-3 sm:grid-cols-2">
                  {inOrdine.map((piano) => (
                    <PianoDaScegliere
                      key={piano.key}
                      piano={piano}
                      attuale={piano.key === attuale}
                      scelto={piano.key === dati.piano}
                      onScegli={scegli}
                    />
                  ))}
                </div>
              </fieldset>
              <div className="space-y-5 border-t border-[#F1F5F9] pt-5">
                <div className="max-w-xs">
                  <Campo
                    id="piano-fine"
                    etichetta="Valido fino al"
                    aiuto={
                      senzaData ? (
                        <span className="text-[#B45309]">
                          Senza data il piano non scade mai: il cliente resta tra quelli «Senza scadenza».
                        </span>
                      ) : (
                        "Alla data il piano non cambia da solo: il cliente compare tra gli scaduti."
                      )
                    }
                  >
                    <input
                      id="piano-fine"
                      type="date"
                      value={dati.fine}
                      onChange={(e) => setDati((prima) => ({ ...prima, fine: e.target.value }))}
                      className={CAMPO_TESTO}
                    />
                  </Campo>
                </div>
                {suMisura(dati.piano) ? (
                  <fieldset className="space-y-3">
                    <legend className="text-xs font-medium text-[#334155]">Limiti su misura</legend>
                    <p className="text-xs leading-5 text-[#64748B]">
                      Vuoto vale come il piano {scelto?.name || ""}, 0 vuol dire illimitato.
                    </p>
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                      {LIMITI.map(({ campo, etichetta }) => (
                        <Campo key={campo} id={`limite-${campo}`} etichetta={etichetta}>
                          <input
                            id={`limite-${campo}`}
                            type="number"
                            min="0"
                            step="1"
                            inputMode="numeric"
                            placeholder={limiteDelPiano(scelto?.limits?.[campo])}
                            value={dati.limiti[campo] ?? ""}
                            onChange={(e) => cambiaLimite(campo, e.target.value)}
                            className={CAMPO_TESTO}
                          />
                        </Campo>
                      ))}
                    </div>
                  </fieldset>
                ) : null}
              </div>
            </div>
            <div className="flex flex-col gap-3 border-t border-[#F1F5F9] px-6 py-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-[#64748B]" aria-live="polite">
                {scelto ? (
                  <>
                    Scelto: <span className="font-semibold text-[#020617]">{scelto.name}</span> · {prezzo(scelto)}
                  </>
                ) : (
                  "Scegli un piano."
                )}
              </p>
              <div className="flex justify-end gap-2">
                <button type="button" className={GRIGIO} onClick={onChiudi} disabled={salva.isPending}>
                  Annulla
                </button>
                <button type="submit" className={SCURO} disabled={salva.isPending || !dati.piano}>
                  {salva.isPending ? "Salvataggio…" : "Salva il piano"}
                </button>
              </div>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Nuova organizzazione
// ---------------------------------------------------------------------------

const VUOTO = { email: "", userName: "", organizationName: "", planId: "" };
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function controlla(dati) {
  const errori = {};
  if (!dati.email.trim()) errori.email = "Scrivi l'email del titolare.";
  else if (!EMAIL.test(dati.email.trim())) errori.email = "Questa email non sembra valida.";
  if (!dati.userName.trim()) errori.userName = "Scrivi il nome del titolare.";
  if (!dati.organizationName.trim()) errori.organizationName = "Scrivi il nome dell'organizzazione.";
  return errori;
}

function prezzo(piano) {
  if (!piano.price_yearly) return suMisura(piano.key) ? "prezzo su misura" : "gratis";
  return `${new Intl.NumberFormat("it-IT").format(piano.price_yearly)} €/anno + IVA`;
}

function NuovaOrganizzazione({ aperta, onChiudi, piani, onCreata }) {
  const { toast } = useToast();
  const [dati, setDati] = useState(VUOTO);
  const [errori, setErrori] = useState({});

  useEffect(() => {
    if (aperta) {
      setDati(VUOTO);
      setErrori({});
    }
  }, [aperta]);

  const crea = useMutation({
    mutationFn: async (corpo) => (await api.post("/admin/organizations", corpo)).data,
    onSuccess: onCreata,
    onError: (e) => toast({ title: "Organizzazione non creata", description: errore(e), variant: "destructive" }),
  });

  const cambia = (campo, valore) => {
    setDati((prima) => ({ ...prima, [campo]: valore }));
    // L'errore sparisce appena si corregge il campo.
    if (errori[campo]) setErrori((prima) => ({ ...prima, [campo]: undefined }));
  };

  const invia = (e) => {
    e.preventDefault();
    const trovati = controlla(dati);
    setErrori(trovati);
    if (Object.keys(trovati).length) return;
    crea.mutate({
      email: dati.email.trim(),
      userName: dati.userName.trim(),
      organizationName: dati.organizationName.trim(),
      planId: dati.planId,
    });
  };

  const campo = (nome) => `${CAMPO_TESTO} ${errori[nome] ? "border-[#FCA5A5] focus:border-[#F87171]" : ""}`;

  return (
    <Dialog open={aperta} onOpenChange={(open) => !open && onChiudi()}>
      <DialogContent className="max-w-lg rounded-[14px]">
        <DialogHeader>
          <DialogTitle>Nuova organizzazione</DialogTitle>
          <DialogDescription>
            Per un cliente seguito da voi: creiamo l'organizzazione e mandiamo al titolare l'invito per entrare.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={invia} noValidate className="space-y-5 pt-2">
          <Campo id="nuova-email" etichetta="Email del titolare" aiuto="Gli arriva l'invito a questo indirizzo." errore={errori.email}>
            <input
              id="nuova-email"
              type="email"
              autoComplete="off"
              placeholder="nome@azienda.it"
              value={dati.email}
              onChange={(e) => cambia("email", e.target.value)}
              aria-invalid={Boolean(errori.email)}
              aria-describedby={errori.email ? "nuova-email-errore" : undefined}
              className={campo("email")}
            />
          </Campo>
          <Campo id="nuova-nome" etichetta="Nome del titolare" errore={errori.userName}>
            <input
              id="nuova-nome"
              type="text"
              autoComplete="off"
              placeholder="Giulia Bianchi"
              value={dati.userName}
              onChange={(e) => cambia("userName", e.target.value)}
              aria-invalid={Boolean(errori.userName)}
              aria-describedby={errori.userName ? "nuova-nome-errore" : undefined}
              className={campo("userName")}
            />
          </Campo>
          <Campo id="nuova-organizzazione" etichetta="Nome dell'organizzazione" errore={errori.organizationName}>
            <input
              id="nuova-organizzazione"
              type="text"
              autoComplete="off"
              placeholder="Studio Bianchi"
              value={dati.organizationName}
              onChange={(e) => cambia("organizationName", e.target.value)}
              aria-invalid={Boolean(errori.organizationName)}
              aria-describedby={errori.organizationName ? "nuova-organizzazione-errore" : undefined}
              className={campo("organizationName")}
            />
          </Campo>
          <Campo id="nuova-piano" etichetta="Piano" aiuto="Senza sceglierlo parte dal piano gratuito. Si cambia quando vuoi con «Cambia piano».">
            <Select value={dati.planId || SENZA_PIANO} onValueChange={(v) => cambia("planId", v === SENZA_PIANO ? "" : v)}>
              <SelectTrigger
                id="nuova-piano"
                className="h-10 rounded-lg border-black/10 bg-white px-3 text-sm text-[#020617] focus:border-[#94A3B8] focus:ring-0 focus:ring-offset-0"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-xl">
                <SelectItem value={SENZA_PIANO}>Piano gratuito</SelectItem>
                {/* Il gratuito c'e' gia' come scelta predefinita. */}
                {piani.filter((piano) => piano.key !== "plan_basic").map((piano) => (
                  <SelectItem key={piano._id} value={piano._id}>
                    {piano.name} · {prezzo(piano)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Campo>
          <DialogFooter className="gap-2 pt-1">
            <button type="button" className={GRIGIO} onClick={onChiudi} disabled={crea.isPending}>
              Annulla
            </button>
            <button type="submit" className={SCURO} disabled={crea.isPending}>
              {crea.isPending ? "Creazione…" : "Crea e invia l'invito"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
